/**
 * unsentPayments.js — durable store for payments that could not be sent.
 *
 * Phase 2B of OWNER_VALUE_PLAN.md, and deliberately NOT an outbox. Nothing here
 * sends anything. A stored payment sits until a human opens the tray and presses
 * Send.
 *
 * THAT IS THE WHOLE DESIGN CONSTRAINT. Phase 2A removed an offline queue because
 * it replayed writes on the client's schedule and let two people end up believing
 * different things about the same row. Re-adding a machine that posts money
 * writes by itself would walk straight back into that. So: a payment kept here is
 * a durable draft with a Send button, not a queued request. On reconnect the app
 * offers; it never acts.
 *
 * WHY localStorage AND NOT IndexedDB. The plan originally called for IndexedDB on
 * the grounds that "localStorage in a closed tab does not reliably survive",
 * which is not true — localStorage persists across tab close, browser restart and
 * device reboot. For a handful of small records it is the simpler choice, it is
 * synchronous (so a draft is on disk before the modal closes, with no await to
 * lose), and the app already keeps drafts this way: see readJsonDraft() and the
 * `_u${UserID}` keys in AddEditInventory. A second persistence mechanism would
 * have bought nothing.
 *
 * Keyed per user, following the same convention, because a shared browser must
 * not show one clerk another clerk's unsent money.
 *
 * Every record carries the ClientRef minted when its form was opened. That is
 * what makes sending later safe: if the payment did reach the server before the
 * connection dropped, the retry collapses onto the same row instead of creating a
 * second one. This module is where Phase 1 earns its keep.
 */

const KEY_PREFIX = 'unsentPayments_u';

const keyFor = (userId) => `${KEY_PREFIX}${userId ?? 'anon'}`;

/**
 * Read the list, never throwing.
 *
 * Same reasoning as readJsonDraft: this is read on the render path, and an
 * exception there escapes to the ErrorBoundary, which re-renders the same code
 * and pins the user on the fallback screen. A corrupt entry is not worth a
 * lockout — but unlike a form draft, this holds money the user believes is
 * saved, so it is quarantined under a `.corrupt` key rather than dropped, and
 * the caller is told. Nothing recovers it automatically; it exists so the data
 * can be read back by hand instead of vanishing.
 */
export function listUnsent(userId) {
  const key = keyFor(userId);
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) throw new Error('not an array');
    return parsed;
  } catch {
    try {
      localStorage.setItem(`${key}.corrupt`, raw);
      localStorage.removeItem(key);
      console.error('unsentPayments: unreadable store quarantined at', `${key}.corrupt`);
    } catch { /* storage is refusing writes too — nothing further to do */ }
    return [];
  }
}

function write(userId, list) {
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(list));
    return true;
  } catch (err) {
    // Quota, or Safari private mode. The caller must not report the payment as
    // kept when it was not.
    console.error('unsentPayments: could not persist', err);
    return false;
  }
}

/**
 * Keep a payment for later. Returns the stored record, or null if storage
 * refused — the caller has to tell the user the truth either way.
 *
 * `clientRef` is required. Storing a payment without one would mean a later send
 * could not be recognised as a retry, which turns this feature into the
 * duplicate generator it exists to prevent.
 */
export function saveUnsent(userId, { body, clientRef, label }) {
  if (!clientRef) {
    console.error('unsentPayments: refusing to store a payment with no ClientRef');
    return null;
  }
  const list = listUnsent(userId);
  // Same ClientRef already held: the user pressed save twice on one payment.
  // Update in place rather than storing it twice.
  const existing = list.find((r) => r.clientRef === clientRef);
  if (existing) {
    existing.body = body;
    existing.label = label;
    return write(userId, list) ? existing : null;
  }
  const record = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    clientRef,
    body,
    label,
    createdAt: new Date().toISOString(),
    lastError: null,
    state: 'unsent',
  };
  list.push(record);
  return write(userId, list) ? record : null;
}

export function removeUnsent(userId, id) {
  const list = listUnsent(userId).filter((r) => r.id !== id);
  return write(userId, list);
}

export function updateUnsent(userId, id, patch) {
  const list = listUnsent(userId);
  const rec = list.find((r) => r.id === id);
  if (!rec) return false;
  Object.assign(rec, patch);
  return write(userId, list);
}

export function countUnsent(userId) {
  return listUnsent(userId).length;
}

// The tray lives in the header while payments are saved from a modal elsewhere
// in the tree, so the two never share a parent. A DOM event is the cheapest way
// for one to tell the other something changed, and it also covers a second tab.
export const UNSENT_CHANGED = 'unsent-payments-changed';
export const notifyUnsentChanged = () => {
  try { window.dispatchEvent(new Event(UNSENT_CHANGED)); } catch { /* SSR/tests */ }
};
